/**
 * Where the GPU jobs (MD and FEP) can compute, per jobtype: the ONE place to
 * flip when a compute target becomes ready for a program.
 *
 * A request's `params.target` only picks the backend queue; the worker on that
 * queue decides how to run. ConiferGPU2 (the in-house GPU server) runs every
 * program below. The cpaws fleet runs OpenFE and TMD today; its STORMM,
 * GROMACS and OpenMM MD programs and GROMACS RBFE are being built (the cpaws
 * plan's P2-P7), and each is enabled here when the cpaws side reports it
 * ready, after the backend's cpaws runner for it exists.
 *
 * Jobtypes are literals (not `JobTypes.*`) for the same reason as
 * FEP_PROGRAMS: the job registry's import chain reaches this module.
 */

import { Loader } from 'BMapsSrc/Loader';

export const COMPUTE_TARGETS = {
    conifergpu2: { key: 'conifergpu2', label: 'ConiferGPU2 (in-house GPU server)' },
    cpaws: { key: 'cpaws', label: 'AWS GPU fleet (cpaws)' },
};

/**
 * ConiferGPU2 is for in-house use: dev and lab builds only (read at call time,
 * so the import cycle through the job registry is harmless).
 * @param {string} target
 * @returns {boolean}
 */
function targetOffered(target) {
    return target !== 'conifergpu2' || !!Loader.AllowLabFeatures;
}

const NOT_YET = 'not available yet';

/**
 * jobtype -> target -> `true` when the target runs it, else why not (shown in
 * the form next to the disabled choice).
 */
export const TARGET_AVAILABILITY = {
    openfe_rbfe: { cpaws: true, conifergpu2: true },
    openfe_abfe: { cpaws: true, conifergpu2: true },
    tmd_rbfe: { cpaws: true, conifergpu2: true },
    gromacs_rbfe: { cpaws: NOT_YET, conifergpu2: true },
    stormm_md: { cpaws: NOT_YET, conifergpu2: true },
    gromacs_md: { cpaws: NOT_YET, conifergpu2: true },
    openmm_md: { cpaws: NOT_YET, conifergpu2: true },
};

/**
 * Whether `target` runs `jobtype`.
 * @param {string} jobtype
 * @param {string} target
 * @returns {boolean}
 */
export function targetAvailable(jobtype, target) {
    return targetOffered(target) && TARGET_AVAILABILITY[jobtype]?.[target] === true;
}

/**
 * Why `target` cannot run `jobtype`, or null when it can.
 * @param {string} jobtype
 * @param {string} target
 * @returns {string|null}
 */
export function targetProblem(jobtype, target) {
    if (!COMPUTE_TARGETS[target]) {
        return `Unknown compute target: ${target}. Use ${Object.keys(COMPUTE_TARGETS).join(' or ')}.`;
    }
    if (targetAvailable(jobtype, target)) return null;
    if (!targetOffered(target)) return `${COMPUTE_TARGETS[target].label} is for in-house use only.`;
    const why = TARGET_AVAILABILITY[jobtype]?.[target];
    return `${COMPUTE_TARGETS[target].label} cannot run this job: ${typeof why === 'string' ? why : 'not supported'}`;
}

/**
 * The target choices for a form, in `order` (those not offered in this build
 * left out), each with `disabled` and the
 * reason in its label when the target cannot run `jobtype`.
 * @param {string} jobtype
 * @param {string[]} [order]
 * @returns {{ key: string, label: string, disabled: boolean }[]}
 */
export function targetOptions(jobtype, order=['conifergpu2', 'cpaws']) {
    return order.filter(targetOffered).map((key) => {
        const ok = targetAvailable(jobtype, key);
        const why = TARGET_AVAILABILITY[jobtype]?.[key];
        const reason = typeof why === 'string' ? why : 'not supported';
        return {
            key,
            label: ok ? COMPUTE_TARGETS[key].label : `${COMPUTE_TARGETS[key].label}: ${reason}`,
            disabled: !ok,
        };
    });
}

/**
 * `preferred` when it can run `jobtype`, else the first target in `order`
 * that can (so switching programs never leaves a disabled target selected).
 * @param {string} jobtype
 * @param {string} [preferred]
 * @param {string[]} [order]
 * @returns {string|undefined}
 */
export function defaultTarget(jobtype, preferred, order=['conifergpu2', 'cpaws']) {
    if (preferred && targetAvailable(jobtype, preferred)) return preferred;
    return order.find((t) => targetAvailable(jobtype, t));
}
